import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  Rating,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
} from '@mui/material'
import { Save, Cancel } from '@mui/icons-material'
import { PickupLocation } from '@otw/models'
import type { Contact } from '@otw/models/leads/contacts'
import type {
  ShopType,
  RepairShopGrade,
  RepairShopLocation,
  RentalCompanyLocation,
  VendorLocation,
  TowCompanyLocation,
} from '@otw/models'
import states from 'states-us'
import { vehicleTypeOptions } from 'constants/inventory'
import { contactToPickupContactFields, findMemberContactForPickupFields } from 'views/SuperAdmin/Management/LeadManagement/util/companyLocationPrefill'

const RENTAL_REGION_OPTIONS = ['Nationwide', 'West Coast', 'East Coast', 'Midwest', 'Northeast', 'Southeast'] as const
const REPAIR_SHOP_GRADES: RepairShopGrade[] = ['A', 'B', 'C']

type LocationFormData =
  | PickupLocation
  | RepairShopLocation
  | (Omit<RentalCompanyLocation, 'id' | 'created_at' | 'updated_at'> & {
      id?: string
      created_at?: unknown
      updated_at?: unknown
    })
  | VendorLocation
  | TowCompanyLocation

interface AddLocationDialogProps {
  open: boolean
  onClose: () => void
  location?: PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation | null
  isRepairShop?: boolean
  isRentalCompany?: boolean
  isVendor?: boolean
  isTowCompany?: boolean
  allowLocationTypeSelection?: boolean // Allow user to choose pickup vs repair shop when creating new
  requireContactInfo?: boolean
  /** When true, only city and/or state are required (street address and ZIP optional). */
  allowPartialAddress?: boolean
  /** Company members — pick primary/secondary contacts for this location. */
  memberContacts?: Contact[]
  onCreate?: (
    data:
      | Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'>
      | Omit<RepairShopLocation, 'id' | 'created_at' | 'updated_at'>
      | Omit<RentalCompanyLocation, 'id' | 'created_at' | 'updated_at'>
      | Omit<VendorLocation, 'id' | 'created_at' | 'updated_at'>
      | Omit<TowCompanyLocation, 'id' | 'created_at' | 'updated_at'>,
  ) => Promise<void>
  onUpdate?: (
    data: PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation,
  ) => Promise<void>
  onError?: (message: string) => void
}

const AddLocationDialog: React.FC<AddLocationDialogProps> = ({
  open,
  onClose,
  location,
  isRepairShop = false,
  isRentalCompany = false,
  isVendor = false,
  isTowCompany = false,
  allowLocationTypeSelection = false,
  requireContactInfo = false,
  allowPartialAddress = false,
  memberContacts,
  onCreate,
  onUpdate,
  onError,
}) => {
  const [selectedLocationType, setSelectedLocationType] = useState<'pickup' | 'repair_shop'>(isRepairShop ? 'repair_shop' : 'pickup')
  const [selectedPrimaryContactId, setSelectedPrimaryContactId] = useState<string | null>(null)
  const [selectedSecondaryContactId, setSelectedSecondaryContactId] = useState<string | null>(null)
  const [contactErrors, setContactErrors] = useState<{
    contact_name?: string
    contact_phone?: string
  }>({})
  // Local validation message shown as an Alert inside the dialog. Previously errors
  // were only routed to onError on the parent, which made them invisible to a user
  // looking at this dialog.
  const [formError, setFormError] = useState<string | null>(null)
  
  const [formData, setFormData] = useState<LocationFormData>({
    company_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    contact_name: '',
    contact_phone: '',
    secondary_contact_name: '',
    secondary_contact_phone: '',
    notes: '',
    ...(isRepairShop && {
      shop_type: [] as ShopType[],
      name: '',
      grade: undefined as RepairShopGrade | undefined,
      communication: 0,
      price: 0,
      speed: 0,
      labor_rate: undefined,
      capacity: undefined as number | undefined,
      diesel: false,
      secondary_phone: '',
      is_active: true,
      inactive_note: null as string | null,
    }),
    ...(isRentalCompany && {
      business_name: '',
      vehicle_types: [] as string[],
      states_rent_in: [] as string[],
      fleet_size: undefined as number | undefined,
      charge_per_week_min: undefined as number | undefined,
      charge_per_week_max: undefined as number | undefined,
    }),
  })

  // Treat locations with IDs starting with "previous-" as new locations (they're from previous addresses)
  const isNewLocation = !location?.id || (location?.id && location.id.startsWith('previous-'))
  const prevOpenRef = useRef(false)

  // Reset form when dialog opens/closes or location changes
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    prevOpenRef.current = open
    if (!justOpened) return

    setContactErrors({})
    setFormError(null)
    if (memberContacts?.length && isNewLocation) {
        setSelectedPrimaryContactId(memberContacts[0]?.id ?? null)
        const secondary = memberContacts.find((c) => c.id !== memberContacts[0]?.id)
        setSelectedSecondaryContactId(secondary?.id ?? null)
      } else if (memberContacts?.length && location && !isNewLocation) {
        const pickupLocation = location as PickupLocation
        const primary = findMemberContactForPickupFields(
          memberContacts,
          pickupLocation.contact_name,
          pickupLocation.contact_phone,
        )
        const secondary = findMemberContactForPickupFields(
          memberContacts,
          pickupLocation.secondary_contact_name,
          pickupLocation.secondary_contact_phone,
          primary ? [primary.id] : [],
        )
        setSelectedPrimaryContactId(primary?.id ?? null)
        setSelectedSecondaryContactId(secondary?.id ?? null)
      } else {
        setSelectedPrimaryContactId(null)
        setSelectedSecondaryContactId(null)
      }
      if (isVendor) {
        if (location && location.id && !location.id.startsWith('previous-')) {
          const v = location as VendorLocation
          setFormData({
            name: v.name || '',
            address: v.address || '',
            city: v.city || '',
            state: v.state || '',
            zip: v.zip || '',
            contact_name: v.contact_name || '',
            contact_phone: v.contact_phone || '',
            notes: v.notes || '',
          } as LocationFormData)
        } else if (location) {
          setFormData({
            name: '',
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            contact_name: location.contact_name || '',
            contact_phone: location.contact_phone || '',
            notes: location.notes || '',
          } as LocationFormData)
        } else {
          setFormData({
            name: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            contact_name: '',
            contact_phone: '',
            notes: '',
          } as LocationFormData)
        }
        return
      }

      if (isTowCompany) {
        if (location && location.id && !location.id.startsWith('previous-')) {
          const t = location as TowCompanyLocation
          setFormData({
            name: t.name || '',
            address: t.address || '',
            city: t.city || '',
            state: t.state || '',
            zip: t.zip || '',
            contact_name: t.contact_name || '',
            contact_phone: t.contact_phone || '',
            notes: t.notes || '',
            grade: t.grade,
            communication: t.communication || 0,
            price: t.price || 0,
            speed: t.speed || 0,
            towing_radius_miles: t.towing_radius_miles,
          } as LocationFormData)
        } else if (location) {
          setFormData({
            name: '',
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            contact_name: location.contact_name || '',
            contact_phone: location.contact_phone || '',
            notes: location.notes || '',
            grade: undefined,
            communication: 0,
            price: 0,
            speed: 0,
            towing_radius_miles: undefined,
          } as LocationFormData)
        } else {
          setFormData({
            name: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            contact_name: '',
            contact_phone: '',
            notes: '',
            grade: undefined,
            communication: 0,
            price: 0,
            speed: 0,
            towing_radius_miles: undefined,
          } as LocationFormData)
        }
        return
      }

      // Set default location type first (before setting formData)
      if (allowLocationTypeSelection && isNewLocation) {
        setSelectedLocationType(isRepairShop ? 'repair_shop' : 'pickup')
      }

      if (location && location.id) {
        // Editing existing location
        const locationData = location as PickupLocation | RepairShopLocation | RentalCompanyLocation
        const isRepairShopLocation = !!(locationData as RepairShopLocation).shop_type
        const isRentalCompanyLocation = 'business_name' in locationData || 'vehicle_types' in locationData || 'states_rent_in' in locationData
        // Handle backward compatibility: convert string shop_type to array if needed
        let shopTypeArray: ShopType[] = []
        if (isRepairShopLocation) {
          const shopType = (locationData as RepairShopLocation).shop_type
          if (Array.isArray(shopType)) {
            shopTypeArray = shopType
          } else if (typeof shopType === 'string') {
            if (shopType !== 'both') {
              shopTypeArray = [shopType as ShopType]
            }
          }
        }
        setFormData({
          ...locationData,
          ...(isRepairShopLocation && {
            shop_type: shopTypeArray,
            communication: (locationData as RepairShopLocation).communication || 0,
            price: (locationData as RepairShopLocation).price || 0,
            speed: (locationData as RepairShopLocation).speed || 0,
            grade: (locationData as RepairShopLocation).grade,
            labor_rate: (locationData as RepairShopLocation).labor_rate,
            capacity: (locationData as RepairShopLocation).capacity,
            diesel: (locationData as RepairShopLocation).diesel || false,
            secondary_phone: (locationData as RepairShopLocation).secondary_phone || '',
            is_active: (locationData as RepairShopLocation).is_active !== false,
            inactive_note: (locationData as RepairShopLocation).inactive_note ?? null,
          }),
          ...(isRentalCompanyLocation && {
            business_name: (locationData as RentalCompanyLocation).business_name || '',
            vehicle_types: Array.isArray((locationData as RentalCompanyLocation).vehicle_types) ? (locationData as RentalCompanyLocation).vehicle_types : [],
            states_rent_in: Array.isArray((locationData as RentalCompanyLocation).states_rent_in) ? (locationData as RentalCompanyLocation).states_rent_in : [],
            fleet_size: (locationData as RentalCompanyLocation).fleet_size,
            charge_per_week_min: (locationData as RentalCompanyLocation).charge_per_week_min,
            charge_per_week_max: (locationData as RentalCompanyLocation).charge_per_week_max,
          }),
        } as LocationFormData)
        if (isRepairShopLocation) {
          setSelectedLocationType('repair_shop')
        } else {
          setSelectedLocationType('pickup')
        }
      } else if (location) {
        // New location with prefilled data (no ID)
        const baseLocationData = {
          company_name: (location as PickupLocation).company_name || '',
          address: location.address || '',
          city: location.city || '',
          state: location.state || '',
          zip: location.zip || '',
          contact_name: location.contact_name || '',
          contact_phone: location.contact_phone || '',
          secondary_contact_name: (location as PickupLocation).secondary_contact_name || '',
          secondary_contact_phone: (location as PickupLocation).secondary_contact_phone || '',
          notes: location.notes || '',
        }

        if (isRentalCompany) {
          setFormData({
            ...baseLocationData,
            business_name: (location as RentalCompanyLocation).business_name || '',
            vehicle_types: Array.isArray((location as RentalCompanyLocation).vehicle_types) ? (location as RentalCompanyLocation).vehicle_types : [],
            states_rent_in: Array.isArray((location as RentalCompanyLocation).states_rent_in) ? (location as RentalCompanyLocation).states_rent_in : [],
            fleet_size: (location as RentalCompanyLocation).fleet_size,
            charge_per_week_min: (location as RentalCompanyLocation).charge_per_week_min,
            charge_per_week_max: (location as RentalCompanyLocation).charge_per_week_max,
          } as LocationFormData)
        } else if (isRepairShop) {
          let shopTypeArray: ShopType[] = []
          const existingShopType = (location as RepairShopLocation).shop_type
          if (Array.isArray(existingShopType)) {
            shopTypeArray = existingShopType
          } else if (typeof existingShopType === 'string' && existingShopType !== 'both') {
            shopTypeArray = [existingShopType as ShopType]
          }
          setFormData({
            ...baseLocationData,
            shop_type: shopTypeArray,
            name: (location as RepairShopLocation).name || '',
            grade: (location as RepairShopLocation).grade,
            communication: (location as RepairShopLocation).communication || 0,
            price: (location as RepairShopLocation).price || 0,
            speed: (location as RepairShopLocation).speed || 0,
            labor_rate: (location as RepairShopLocation).labor_rate,
            capacity: (location as RepairShopLocation).capacity,
            diesel: (location as RepairShopLocation).diesel || false,
            secondary_phone: (location as RepairShopLocation).secondary_phone || '',
            is_active: (location as RepairShopLocation).is_active !== false,
            inactive_note: (location as RepairShopLocation).inactive_note ?? null,
          } as LocationFormData)
        } else {
          setFormData(baseLocationData as LocationFormData)
        }
      } else {
        // Empty form
        setFormData({
          company_name: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          contact_name: '',
          contact_phone: '',
          secondary_contact_name: '',
          secondary_contact_phone: '',
          notes: '',
          ...(isRepairShop && {
            shop_type: [] as ShopType[],
            name: '',
            communication: 0,
            price: 0,
            speed: 0,
            labor_rate: undefined,
            capacity: undefined,
            diesel: false,
            secondary_phone: '',
            is_active: true,
            inactive_note: null,
          }),
          ...(isRentalCompany && {
            business_name: '',
            vehicle_types: [] as string[],
            states_rent_in: [] as string[],
            fleet_size: undefined,
            charge_per_week_min: undefined,
            charge_per_week_max: undefined,
          }),
        } as LocationFormData)
      }
  }, [open, location, isRepairShop, isRentalCompany, isVendor, isTowCompany, allowLocationTypeSelection, isNewLocation, memberContacts])
  
  // Calculate effective repair shop status based on selected type (rental company tab overrides)
  const effectiveIsRepairShop =
    !isRentalCompany && !isVendor && !isTowCompany && (allowLocationTypeSelection ? selectedLocationType === 'repair_shop' : isRepairShop)

  const showMemberContactPickers =
    Boolean(memberContacts?.length) &&
    !effectiveIsRepairShop &&
    !isRentalCompany &&
    !isVendor &&
    !isTowCompany

  const applyPrimaryContact = (contact: Contact | null) => {
    const { name, phone } = contactToPickupContactFields(contact)
    setFormData((prev) => ({
      ...prev,
      contact_name: name,
      contact_phone: phone ? formatPhoneNumber(phone) : '',
    }))
    if (name.trim() || phone.trim()) {
      setContactErrors((prev) => ({ ...prev, contact_name: undefined, contact_phone: undefined }))
    }
  }

  const applySecondaryContact = (contact: Contact | null) => {
    const { name, phone } = contactToPickupContactFields(contact)
    setFormData((prev) => ({
      ...prev,
      secondary_contact_name: name,
      secondary_contact_phone: phone ? formatPhoneNumber(phone) : '',
    }))
  }

  // Update form data when location type changes (for repair shop fields)
  useEffect(() => {
    if (open && allowLocationTypeSelection && isNewLocation && !isVendor && !isTowCompany) {
      if (effectiveIsRepairShop) {
        // Switching to repair shop - add shop_type and name if not present
        setFormData(prev => {
          if ((prev as any).shop_type) {
            // Handle backward compatibility: convert string shop_type to array if needed
            const existingShopType = (prev as any).shop_type
            if (Array.isArray(existingShopType)) {
              return prev // Already has shop_type as array
            } else if (typeof existingShopType === 'string') {
              // Convert old string format to array, excluding 'both'
              const shopTypeArray = existingShopType !== 'both' 
                ? [existingShopType as ShopType]
                : []
              return {
                ...prev,
                shop_type: shopTypeArray,
              }
            }
            return prev
          }
          return {
            ...prev,
            shop_type: [] as ShopType[],
            name: '',
            grade: undefined as RepairShopGrade | undefined,
            communication: (prev as RepairShopLocation).communication || 0,
            price: (prev as RepairShopLocation).price || 0,
            speed: (prev as RepairShopLocation).speed || 0,
            labor_rate: (prev as RepairShopLocation).labor_rate,
            capacity: (prev as RepairShopLocation).capacity,
            diesel: (prev as RepairShopLocation).diesel || false,
            secondary_phone: (prev as RepairShopLocation).secondary_phone || '',
          }
        })
      } else {
        // Switching to pickup - remove shop_type, name, rating fields, labor_rate, capacity, diesel, and secondary_phone
        setFormData(prev => {
          const { shop_type, name, grade, communication, price, speed, labor_rate, capacity, diesel, secondary_phone, ...rest } = prev as any
          return rest
        })
      }
    }
  }, [effectiveIsRepairShop, open, allowLocationTypeSelection, isNewLocation, isVendor, isTowCompany])

  // Handle form changes
  const handleFormChange = (
    field: keyof PickupLocation | keyof RepairShopLocation | keyof RentalCompanyLocation | keyof VendorLocation | keyof TowCompanyLocation,
    value: string | number | ShopType[] | RepairShopGrade | string[] | boolean | null | undefined,
  ) => {
    if ((field === 'contact_name' || field === 'contact_phone') && typeof value === 'string' && value.trim()) {
      setContactErrors(prev => ({ ...prev, [field]: undefined }))
    }
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  // Handle shop type chip toggle
  const handleShopTypeToggle = (type: ShopType) => {
    setFormData(prev => {
      const currentShopTypes = (prev as RepairShopLocation).shop_type || []
      const isSelected = currentShopTypes.includes(type)
      const newShopTypes = isSelected
        ? currentShopTypes.filter(t => t !== type)
        : [...currentShopTypes, type]
      return {
        ...prev,
        shop_type: newShopTypes,
      }
    })
  }

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '')
    
    // Limit to 10 digits
    const limitedNumbers = numbers.slice(0, 10)
    
    // Format based on length
    if (limitedNumbers.length === 0) return ''
    if (limitedNumbers.length <= 3) return `(${limitedNumbers}`
    if (limitedNumbers.length <= 6) return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`
    return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`
  }

  // Handle phone number change with formatting
  const handlePhoneChange = (
    field: 'contact_phone' | 'secondary_phone' | 'secondary_contact_phone',
    value: string
  ) => {
    const formatted = formatPhoneNumber(value)
    handleFormChange(field, formatted)
  }

  // Handle save
  const handleSave = async () => {
    setFormError(null)
    const fail = (message: string) => {
      setFormError(message)
      onError?.(message)
    }

    const address = formData.address?.trim() ?? ''
    const city = formData.city?.trim() ?? ''
    const state = formData.state?.trim() ?? ''
    const zip = formData.zip?.trim() ?? ''

    if (allowPartialAddress) {
      if (!city && !state) {
        fail('Enter at least a city or state (e.g. for timezone reference)')
        return
      }
    } else if (!address || !city || !state || !zip) {
      fail('Please fill in all required address fields')
      return
    }

    if (requireContactInfo) {
      const nextContactErrors = {
        ...(!formData.contact_name?.trim() ? { contact_name: 'Contact name is required' } : {}),
        ...(!formData.contact_phone?.trim() ? { contact_phone: 'Contact phone is required' } : {}),
      }

      setContactErrors(nextContactErrors)
      if (Object.keys(nextContactErrors).length > 0) {
        fail('Please enter a contact name and phone number')
        return
      }
    }

    if (effectiveIsRepairShop) {
      const shopTypes = (formData as RepairShopLocation).shop_type || []
      if (shopTypes.length === 0) {
        fail('Please select at least one shop type')
        return
      }
      if (!(formData as RepairShopLocation).name?.trim()) {
        fail('Please enter a shop name')
        return
      }
    }

    if (isRentalCompany) {
      if (!(formData as RentalCompanyLocation).business_name?.trim()) {
        fail('Please enter a business name')
        return
      }
      const vehicleTypes = (formData as RentalCompanyLocation).vehicle_types || []
      if (vehicleTypes.length === 0) {
        fail('Please add at least one vehicle type')
        return
      }
    }

    if (isVendor) {
      if (!(formData as VendorLocation).name?.trim()) {
        fail('Please enter a vendor name')
        return
      }
    }

    if (isTowCompany) {
      if (!(formData as TowCompanyLocation).name?.trim()) {
        fail('Please enter a tow company name')
        return
      }
    }

    try {
      const normalizedNotes = formData.notes?.trim() || undefined
      if (isNewLocation) {
        if (isVendor) {
          const vendorData: Omit<VendorLocation, 'id' | 'created_at' | 'updated_at'> = {
            name: (formData as VendorLocation).name!.trim(),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onCreate?.(vendorData)
        } else if (isTowCompany) {
          const radius = (formData as TowCompanyLocation).towing_radius_miles
          const towData: Omit<TowCompanyLocation, 'id' | 'created_at' | 'updated_at'> = {
            name: (formData as TowCompanyLocation).name!.trim(),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            communication: (formData as TowCompanyLocation).communication || 0,
            price: (formData as TowCompanyLocation).price || 0,
            speed: (formData as TowCompanyLocation).speed || 0,
            ...((formData as TowCompanyLocation).grade
              ? { grade: (formData as TowCompanyLocation).grade }
              : {}),
            ...(radius !== undefined && radius !== null && !Number.isNaN(radius)
              ? { towing_radius_miles: radius }
              : {}),
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onCreate?.(towData)
        } else if (isRentalCompany) {
          const rentalData: Omit<RentalCompanyLocation, 'id' | 'created_at' | 'updated_at'> = {
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            business_name: (formData as RentalCompanyLocation).business_name!,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            vehicle_types: (formData as RentalCompanyLocation).vehicle_types || [],
            states_rent_in: (formData as RentalCompanyLocation).states_rent_in || [],
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
            ...((formData as RentalCompanyLocation).fleet_size !== undefined && (formData as RentalCompanyLocation).fleet_size !== null
              ? { fleet_size: (formData as RentalCompanyLocation).fleet_size }
              : {}),
            ...((formData as RentalCompanyLocation).charge_per_week_min !== undefined && (formData as RentalCompanyLocation).charge_per_week_min !== null
              ? { charge_per_week_min: (formData as RentalCompanyLocation).charge_per_week_min }
              : {}),
            ...((formData as RentalCompanyLocation).charge_per_week_max !== undefined && (formData as RentalCompanyLocation).charge_per_week_max !== null
              ? { charge_per_week_max: (formData as RentalCompanyLocation).charge_per_week_max }
              : {}),
          }
          await onCreate?.(rentalData)
        } else if (effectiveIsRepairShop) {
          const isActive = (formData as RepairShopLocation).is_active !== false
          const inactiveNote =
            typeof (formData as RepairShopLocation).inactive_note === 'string'
              ? (formData as RepairShopLocation).inactive_note!.trim() || null
              : null
          const repairData: Omit<RepairShopLocation, 'id' | 'created_at' | 'updated_at'> = {
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
            shop_type: (formData as RepairShopLocation).shop_type!,
            name: (formData as RepairShopLocation).name || '',
            communication: (formData as RepairShopLocation).communication || 0,
            price: (formData as RepairShopLocation).price || 0,
            speed: (formData as RepairShopLocation).speed || 0,
            ...((formData as RepairShopLocation).grade
              ? { grade: (formData as RepairShopLocation).grade }
              : {}),
            diesel: (formData as RepairShopLocation).diesel || false,
            secondary_phone: (formData as RepairShopLocation).secondary_phone || '',
            ...((formData as RepairShopLocation).labor_rate !== undefined && (formData as RepairShopLocation).labor_rate !== null
              ? { labor_rate: (formData as RepairShopLocation).labor_rate }
              : {}),
            ...((formData as RepairShopLocation).capacity !== undefined && (formData as RepairShopLocation).capacity !== null
              ? { capacity: (formData as RepairShopLocation).capacity }
              : {}),
            is_active: isActive,
            inactive_at: isActive ? null : new Date(),
            inactive_note: inactiveNote,
          }
          await onCreate?.(repairData)
        } else {
          const pickupData: Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'> = {
            company_name: (formData as PickupLocation).company_name?.trim() || undefined,
            address,
            city,
            state,
            zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            secondary_contact_name: (formData as PickupLocation).secondary_contact_name?.trim() || undefined,
            secondary_contact_phone: (formData as PickupLocation).secondary_contact_phone?.trim() || undefined,
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onCreate?.(pickupData)
        }
      } else if (location?.id) {
        if (isVendor) {
          const vendorData: VendorLocation = {
            id: location.id,
            name: (formData as VendorLocation).name!.trim(),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onUpdate?.(vendorData)
        } else if (isTowCompany) {
          const radius = (formData as TowCompanyLocation).towing_radius_miles
          const towData: TowCompanyLocation = {
            id: location.id,
            name: (formData as TowCompanyLocation).name!.trim(),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            communication: (formData as TowCompanyLocation).communication || 0,
            price: (formData as TowCompanyLocation).price || 0,
            speed: (formData as TowCompanyLocation).speed || 0,
            ...((formData as TowCompanyLocation).grade
              ? { grade: (formData as TowCompanyLocation).grade }
              : {}),
            ...(radius !== undefined && radius !== null && !Number.isNaN(radius)
              ? { towing_radius_miles: radius }
              : {}),
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onUpdate?.(towData)
        } else if (isRentalCompany) {
          const rentalData: RentalCompanyLocation = {
            id: location.id,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            business_name: (formData as RentalCompanyLocation).business_name!,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            vehicle_types: (formData as RentalCompanyLocation).vehicle_types || [],
            states_rent_in: (formData as RentalCompanyLocation).states_rent_in || [],
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
            ...((formData as RentalCompanyLocation).fleet_size !== undefined &&
            (formData as RentalCompanyLocation).fleet_size !== null
              ? { fleet_size: (formData as RentalCompanyLocation).fleet_size }
              : {}),
            ...((formData as RentalCompanyLocation).charge_per_week_min !== undefined &&
            (formData as RentalCompanyLocation).charge_per_week_min !== null
              ? { charge_per_week_min: (formData as RentalCompanyLocation).charge_per_week_min }
              : {}),
            ...((formData as RentalCompanyLocation).charge_per_week_max !== undefined &&
            (formData as RentalCompanyLocation).charge_per_week_max !== null
              ? { charge_per_week_max: (formData as RentalCompanyLocation).charge_per_week_max }
              : {}),
          }
          await onUpdate?.(rentalData)
        } else if (effectiveIsRepairShop) {
          const isActive = (formData as RepairShopLocation).is_active !== false
          const inactiveNote =
            typeof (formData as RepairShopLocation).inactive_note === 'string'
              ? (formData as RepairShopLocation).inactive_note!.trim() || null
              : null
          const wasActive = (location as RepairShopLocation).is_active !== false
          const repairData: RepairShopLocation = {
            id: location.id,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
            shop_type: (formData as RepairShopLocation).shop_type!,
            name: (formData as RepairShopLocation).name || '',
            communication: (formData as RepairShopLocation).communication || 0,
            price: (formData as RepairShopLocation).price || 0,
            speed: (formData as RepairShopLocation).speed || 0,
            ...((formData as RepairShopLocation).grade
              ? { grade: (formData as RepairShopLocation).grade }
              : {}),
            diesel: (formData as RepairShopLocation).diesel || false,
            secondary_phone: (formData as RepairShopLocation).secondary_phone || '',
            ...((formData as RepairShopLocation).labor_rate !== undefined && (formData as RepairShopLocation).labor_rate !== null
              ? { labor_rate: (formData as RepairShopLocation).labor_rate }
              : {}),
            ...((formData as RepairShopLocation).capacity !== undefined && (formData as RepairShopLocation).capacity !== null
              ? { capacity: (formData as RepairShopLocation).capacity }
              : {}),
            is_active: isActive,
            inactive_at: isActive
              ? null
              : wasActive
                ? new Date()
                : (location as RepairShopLocation).inactive_at ?? new Date(),
            inactive_note: inactiveNote,
          }
          await onUpdate?.(repairData)
        } else {
          const pickupData: PickupLocation = {
            id: location.id,
            ...((formData as PickupLocation).company_name?.trim()
              ? { company_name: (formData as PickupLocation).company_name!.trim() }
              : {}),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            ...((formData as PickupLocation).secondary_contact_name?.trim()
              ? { secondary_contact_name: (formData as PickupLocation).secondary_contact_name!.trim() }
              : {}),
            ...((formData as PickupLocation).secondary_contact_phone?.trim()
              ? { secondary_contact_phone: (formData as PickupLocation).secondary_contact_phone!.trim() }
              : {}),
            ...(normalizedNotes ? { notes: normalizedNotes } : {}),
          }
          await onUpdate?.(pickupData)
        }
      }
      onClose()
    } catch (error) {
      console.error('Error saving location:', error)
      const message = error instanceof Error ? error.message : 'Error saving location'
      setFormError(message)
      onError?.(message)
    }
  }

  // Get dialog title
  const getDialogTitle = () => {
    if (isNewLocation && allowLocationTypeSelection && !isRentalCompany && !isVendor && !isTowCompany) {
      return 'Save Location'
    }
    const locationType = isVendor
      ? 'Vendor'
      : isTowCompany
        ? 'Tow Company'
        : isRentalCompany
        ? 'Rental Company'
        : effectiveIsRepairShop
          ? 'Repair Shop'
          : 'Customer Location'
    return isNewLocation ? `Add New ${locationType}` : `Edit ${locationType}`
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            {allowLocationTypeSelection && isNewLocation && !isVendor && !isTowCompany && (
              <FormControl fullWidth>
                <InputLabel>Location Type</InputLabel>
                <Select
                  value={selectedLocationType}
                  onChange={(e) => {
                    const newType = e.target.value as 'pickup' | 'repair_shop'
                    setSelectedLocationType(newType)
                  }}
                  label="Location Type"
                >
                  <MenuItem value="pickup">Customer Location</MenuItem>
                  <MenuItem value="repair_shop">Repair Shop</MenuItem>
                </Select>
              </FormControl>
            )}
            {effectiveIsRepairShop && (
              <>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Shop Type <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(['mechanic_shop', 'body_shop', 'parts', 'other', 'mobile'] as ShopType[]).map((type) => {
                      const isSelected = ((formData as RepairShopLocation).shop_type || []).includes(type)
                      const labels: Record<ShopType, string> = {
                        mechanic_shop: 'Mechanic Shop',
                        body_shop: 'Body Shop',
                        parts: 'Parts',
                        other: 'Other',
                        mobile: 'Mobile',
                      }
                      return (
                        <Chip
                          key={type}
                          label={labels[type]}
                          onClick={() => handleShopTypeToggle(type)}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      )
                    })}
                  </Stack>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(formData as RepairShopLocation).diesel || false}
                      onChange={(e) => handleFormChange('diesel', e.target.checked)}
                    />
                  }
                  label="Diesel"
                />
                <TextField
                  fullWidth
                  label="Shop Name"
                  value={(formData as RepairShopLocation).name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="e.g., Joe's Auto Repair, Downtown Body Shop"
                  required
                  helperText="Enter the name of the repair shop"
                />
                <FormControl fullWidth>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={(formData as RepairShopLocation).grade || ''}
                    onChange={(e) => handleFormChange('grade', (e.target.value || undefined) as RepairShopGrade | undefined)}
                    label="Grade"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {REPAIR_SHOP_GRADES.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Labor Rate"
                  type="number"
                  value={(formData as RepairShopLocation).labor_rate || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseFloat(e.target.value)
                    handleFormChange('labor_rate', value)
                  }}
                  placeholder="e.g., 150.00"
                  helperText="Optional: Enter the labor rate per hour (e.g., 150.00)"
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={(formData as RepairShopLocation).capacity ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                    const value = raw !== undefined && !Number.isNaN(raw) ? raw : undefined
                    handleFormChange('capacity', value)
                  }}
                  placeholder="e.g., 10"
                  helperText="Optional: Capacity (e.g. number of vehicles or bays)"
                  inputProps={{ min: 0, step: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Communication Rating
                    </Typography>
                    <Rating
                      value={(formData as RepairShopLocation).communication || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('communication', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Price Rating
                    </Typography>
                    <Rating
                      value={(formData as RepairShopLocation).price || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('price', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Speed Rating
                    </Typography>
                    <Rating
                      value={(formData as RepairShopLocation).speed || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('speed', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                </Box>
              </>
            )}
            {isVendor && (
              <TextField
                fullWidth
                label="Vendor name"
                value={(formData as VendorLocation).name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g., Acme Parts Supply"
                required
                helperText="Name of the vendor or supplier"
              />
            )}
            {isTowCompany && (
              <>
                <TextField
                  fullWidth
                  label="Company name"
                  value={(formData as TowCompanyLocation).name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="e.g., ABC Towing"
                  required
                  helperText="Name of the tow company"
                />
                <FormControl fullWidth>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={(formData as TowCompanyLocation).grade || ''}
                    onChange={(e) =>
                      handleFormChange('grade', (e.target.value || undefined) as RepairShopGrade | undefined)
                    }
                    label="Grade"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {REPAIR_SHOP_GRADES.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Communication Rating
                    </Typography>
                    <Rating
                      value={(formData as TowCompanyLocation).communication || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('communication', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Price Rating
                    </Typography>
                    <Rating
                      value={(formData as TowCompanyLocation).price || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('price', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Speed Rating
                    </Typography>
                    <Rating
                      value={(formData as TowCompanyLocation).speed || 0}
                      onChange={(_, newValue) => {
                        handleFormChange('speed', newValue || 0)
                      }}
                      max={5}
                      size="large"
                    />
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  label="Towing radius (miles)"
                  type="number"
                  value={(formData as TowCompanyLocation).towing_radius_miles ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value === '' ? undefined : parseFloat(e.target.value)
                    const value = raw !== undefined && !Number.isNaN(raw) ? raw : undefined
                    handleFormChange('towing_radius_miles', value)
                  }}
                  placeholder="e.g., 50"
                  helperText="How far this company will tow, in miles"
                  inputProps={{ min: 0, step: 1 }}
                />
              </>
            )}
            {!effectiveIsRepairShop && !isRentalCompany && !isVendor && !isTowCompany && (
              <>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={(formData as PickupLocation).company_name || ''}
                  onChange={(e) => handleFormChange('company_name', e.target.value)}
                  placeholder="e.g., Acme Logistics"
                  helperText="Optional: customer company name for this pickup location"
                />
              </>
            )}
            {isRentalCompany && (
              <>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={(formData as RentalCompanyLocation).business_name || ''}
                  onChange={(e) => handleFormChange('business_name', e.target.value)}
                  placeholder="e.g., ABC Rentals"
                  required
                  helperText="Enter the rental company business name"
                />
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={vehicleTypeOptions}
                  value={(formData as RentalCompanyLocation).vehicle_types || []}
                  onChange={(_, newValue) => handleFormChange('vehicle_types', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Types of vehicles"
                      required
                      helperText="Add vehicle types (e.g. Sedan, SUV, Truck). Type and press Enter to add custom."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    States they rent in
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    {RENTAL_REGION_OPTIONS.map((region) => {
                      const current = (formData as RentalCompanyLocation).states_rent_in || []
                      const isSelected = current.length === 1 && current[0] === region
                      return (
                        <Button
                          key={region}
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleFormChange('states_rent_in', [region])}
                        >
                          {region}
                        </Button>
                      )
                    })}
                  </Stack>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableCloseOnSelect
                    options={states}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.abbreviation)}
                    value={
                      (formData as RentalCompanyLocation).states_rent_in?.map((item) => {
                        const stateObj = states.find((s) => s.abbreviation === item)
                        if (stateObj) return stateObj
                        return item
                      }) ?? []
                    }
                    onChange={(_, newValue) => {
                      handleFormChange(
                        'states_rent_in',
                        newValue.map((s) => (typeof s === 'string' ? s : s.abbreviation))
                      )
                    }}
                    isOptionEqualToValue={(option, value) =>
                      (typeof option === 'string' ? option : option.abbreviation) ===
                      (typeof value === 'string' ? value : value?.abbreviation)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Or select states"
                        placeholder="Select states"
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2">
                          {typeof option === 'string' ? option : `${option.name} (${option.abbreviation})`}
                        </Typography>
                      </Box>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options
                      const searchTerm = inputValue.toLowerCase()
                      return options.filter((option) => {
                        const name = (option as { name?: string; abbreviation?: string }).name?.toLowerCase()
                        const abbreviation = (option as { name?: string; abbreviation?: string }).abbreviation?.toLowerCase()
                        return name?.includes(searchTerm) || abbreviation?.includes(searchTerm)
                      })
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Number of trucks in fleet"
                  type="number"
                  value={(formData as RentalCompanyLocation).fleet_size ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                    const value = raw !== undefined && !Number.isNaN(raw) ? raw : undefined
                    handleFormChange('fleet_size', value)
                  }}
                  placeholder="e.g., 25"
                  helperText="Optional: Total number of trucks in their fleet"
                  inputProps={{ min: 0, step: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Charge per week (min)"
                    type="number"
                    value={(formData as RentalCompanyLocation).charge_per_week_min ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value === '' ? undefined : parseFloat(e.target.value)
                      const value = raw !== undefined && !Number.isNaN(raw) ? raw : undefined
                      handleFormChange('charge_per_week_min', value)
                    }}
                    placeholder="e.g., 500"
                    helperText="Optional: Minimum $ per week"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Charge per week (max)"
                    type="number"
                    value={(formData as RentalCompanyLocation).charge_per_week_max ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value === '' ? undefined : parseFloat(e.target.value)
                      const value = raw !== undefined && !Number.isNaN(raw) ? raw : undefined
                      handleFormChange('charge_per_week_max', value)
                    }}
                    placeholder="e.g., 1200"
                    helperText="Optional: Maximum $ per week"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Box>
              </>
            )}

            {allowPartialAddress && !effectiveIsRepairShop && !isRentalCompany && !isVendor && !isTowCompany ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                You can save with only a city and/or state when the full street address is not known yet.
              </Alert>
            ) : null}

            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              required={!allowPartialAddress}
              helperText={
                allowPartialAddress ? 'Optional when city or state is provided' : undefined
              }
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleFormChange('city', e.target.value)}
                required={!allowPartialAddress}
                helperText={allowPartialAddress ? 'City and/or state required' : undefined}
              />
              <Autocomplete
                options={states}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option
                  }
                  return `${option.abbreviation}`
                }}
                onChange={(_, newValue) => {
                  handleFormChange('state', newValue ? newValue.abbreviation : '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    required={!allowPartialAddress}
                    helperText={allowPartialAddress ? 'City and/or state required' : undefined}
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.abbreviation === value || option.name === value
                  }
                  return option.abbreviation === value.abbreviation
                }}
                value={
                  formData.state
                    ? states.find(state => state.abbreviation === formData.state) || null
                    : null
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2">
                      {option.name} ({option.abbreviation})
                    </Typography>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options
                  
                  const searchTerm = inputValue.toLowerCase()
                  return options.filter((option) => {
                    const name = option.name.toLowerCase()
                    const abbreviation = option.abbreviation.toLowerCase()
                    return name.includes(searchTerm) || abbreviation.includes(searchTerm)
                  })
                }}
                sx={{ minWidth: 120 }}
              />
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zip}
                onChange={(e) => handleFormChange('zip', e.target.value)}
                required={!allowPartialAddress}
                helperText={allowPartialAddress ? 'Optional' : undefined}
              />
            </Box>

            {showMemberContactPickers ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Contacts at this company
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={memberContacts ?? []}
                    value={
                      memberContacts?.find((c) => c.id === selectedPrimaryContactId) ?? null
                    }
                    onChange={(_, contact) => {
                      setSelectedPrimaryContactId(contact?.id ?? null)
                      applyPrimaryContact(contact)
                      if (contact?.id === selectedSecondaryContactId) {
                        setSelectedSecondaryContactId(null)
                        applySecondaryContact(null)
                      }
                    }}
                    getOptionLabel={(option) => option.name || 'Unnamed contact'}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Primary contact" placeholder="Select contact" />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name || 'Unnamed contact'}
                        </Typography>
                        {option.email || option.phone ? (
                          <Typography variant="caption" color="text.secondary">
                            {[option.email, option.phone].filter(Boolean).join(' • ')}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={(memberContacts ?? []).filter(
                      (c) => c.id !== selectedPrimaryContactId,
                    )}
                    value={
                      memberContacts?.find((c) => c.id === selectedSecondaryContactId) ?? null
                    }
                    onChange={(_, contact) => {
                      setSelectedSecondaryContactId(contact?.id ?? null)
                      applySecondaryContact(contact)
                    }}
                    getOptionLabel={(option) => option.name || 'Unnamed contact'}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Secondary contact"
                        placeholder="Optional"
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name || 'Unnamed contact'}
                        </Typography>
                        {option.email || option.phone ? (
                          <Typography variant="caption" color="text.secondary">
                            {[option.email, option.phone].filter(Boolean).join(' • ')}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  />
                </Box>
              </>
            ) : null}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contact_name}
                onChange={(e) => handleFormChange('contact_name', e.target.value)}
                required={requireContactInfo}
                error={!!contactErrors.contact_name}
                helperText={contactErrors.contact_name}
              />
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => handlePhoneChange('contact_phone', e.target.value)}
                placeholder="(555) 123-4567"
                required={requireContactInfo}
                error={!!contactErrors.contact_phone}
                helperText={contactErrors.contact_phone}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Box>
            {!effectiveIsRepairShop && !isRentalCompany && !isVendor && !isTowCompany && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Secondary Contact Name"
                  value={(formData as PickupLocation).secondary_contact_name || ''}
                  onChange={(e) => handleFormChange('secondary_contact_name', e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Secondary Contact Phone"
                  value={(formData as PickupLocation).secondary_contact_phone || ''}
                  onChange={(e) => handlePhoneChange('secondary_contact_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              </Box>
            )}
            {effectiveIsRepairShop && (
              <TextField
                fullWidth
                label="Secondary Phone"
                value={(formData as RepairShopLocation).secondary_phone || ''}
                onChange={(e) => handlePhoneChange('secondary_phone', e.target.value)}
                placeholder="(555) 123-4567"
                helperText="Optional: Enter a secondary phone number for this repair shop"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            )}

            {effectiveIsRepairShop && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={(formData as RepairShopLocation).is_active === false}
                      onChange={(e) => {
                        const inactive = e.target.checked
                        handleFormChange('is_active', !inactive)
                        if (!inactive) {
                          // Reactivating — keep inactive_note unless user clears it
                        }
                      }}
                    />
                  }
                  label="No longer in use"
                />
                <TextField
                  fullWidth
                  label="Inactive note"
                  value={(formData as RepairShopLocation).inactive_note ?? ''}
                  onChange={(e) => handleFormChange('inactive_note', e.target.value || null)}
                  multiline
                  rows={2}
                  disabled={(formData as RepairShopLocation).is_active !== false}
                  helperText={
                    (formData as RepairShopLocation).is_active === false
                      ? 'Optional reason this shop is no longer used. Kept if you reactivate unless cleared.'
                      : 'Enable “No longer in use” to add a note.'
                  }
                />
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes ?? ''}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              multiline
              rows={3}
              helperText="Optional"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Cancel />}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={<Save />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddLocationDialog

